import { inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { injectSearchManager, injectTabManager, TabConfig } from './index';

import { tap } from 'rxjs';

export interface TabSearchConfig {
  minSearchLength?: number;
  debounceTime?: number;
}

/**
 * creates a Tab search manager that filters tabs based on form field labels
 * works in conjunction with TabManager to provide search functionality
 */
export function tabSearchManager(
  tabManager: ReturnType<typeof injectTabManager>,
  config?: TabSearchConfig,
) {
  const searchManager = injectSearchManager({
    debounceTime: config?.debounceTime ?? 150,
  });
  const currentSearchString = signal<string>('');
  const hasVisibleTabs = signal<boolean>(true);
  const destroyRef = inject(DestroyRef);

  const MIN_SEARCH_LENGTH = config?.minSearchLength ?? 1;

  function getMatchingTabIds(
    searchString: string,
    tabs: TabConfig[],
  ): string[] {
    // If search is too short, return all Tabs
    if (searchString.length < MIN_SEARCH_LENGTH) {
      return tabs.map((t) => t.id);
    }

    const matchingIds: string[] = [];
    const searchLower = searchString.toLowerCase();

    tabs.forEach((tab) => {
      // ! only check Tabs with form components -> non-form Tabs always stay visible
      if (!tabManager.hasFormProperties(tab.id)) {
        // ! uncomment below to test the filtering for OutputSettingsFormatEditorComponent
        // matchingIds.push(tab.id);
        return;
      }

      const component = tabManager.getTabComponent(tab.id);

      if (component?.formProperties) {
        const hasMatch = component.formProperties.some((prop) =>
          prop.propertyLabel?.toLowerCase().includes(searchLower),
        );

        if (hasMatch) {
          matchingIds.push(tab.id);
        }
      }
    });

    return matchingIds;
  }

  //* auto-switching -> when search changes
  function setupAutoSwitch(tabs: TabConfig[]): void {
    searchManager.searchString$
      .pipe(
        tap((searchString) => {
          currentSearchString.set(searchString);

          const matchingIds = getMatchingTabIds(searchString, tabs);
          const currentTabId = tabManager.activeTabId();

          // Update visibility state
          hasVisibleTabs.set(matchingIds.length > 0);

          // switch to first matching Tab - if any
          if (
            matchingIds.length > 0 &&
            currentTabId &&
            !matchingIds.includes(currentTabId)
          ) {
            tabManager.selectTab(matchingIds[0]);
          }
        }),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe();
  }

  //* returns Тabs filtered by current search
  function getVisibleTabs(tabs: TabConfig[]): TabConfig[] {
    const searchString = currentSearchString();
    const matchingIds = getMatchingTabIds(searchString, tabs);
    return tabs.filter((tab) => matchingIds.includes(tab.id));
  }

  return {
    // * Search state
    searchString$: searchManager.searchString$,
    currentSearchString: currentSearchString.asReadonly(),
    hasVisibleTabs: hasVisibleTabs.asReadonly(),

    // * Search actions
    onSearchInput: searchManager.onSearchInput,
    setupAutoSwitch,

    // * Tab filtering
    getVisibleTabs,
  };
}
